import React, { useRef, useMemo } from 'react';
import { PageHeader, Button, EmptyState, TYPE, cn } from './ui';
import { CreditCard, Loan, PayoffPlan } from '../types';
import { projectCardBalance, activeCoveringPlan } from '../utils/finance/cardLedger';
import { minimumPaymentForCard } from '../utils/finance/minimums';

interface DebtSheetProps {
  creditCards: CreditCard[];
  loans: Loan[];
  plans: PayoffPlan[];
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

const formatPercent = (value: number) => `${value.toFixed(2)}%`;

const formatDate = (date?: Date | string) => {
  if (!date) return '—';
  const d = new Date(date);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const ordinalDay = (day?: number) => {
  if (!day) return '—';
  const s = ['th', 'st', 'nd', 'rd'];
  const v = day % 100;
  return `${day}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

export const DebtSheet: React.FC<DebtSheetProps> = ({
  creditCards,
  loans,
  plans,
}) => {
  const printRef = useRef<HTMLDivElement>(null);

  // Previously `creditCards.filter(() => true)` — a fresh array identity every
  // render, so every downstream useMemo re-ran and cached nothing.
  const activeCards = creditCards;
  const activeLoans = useMemo(() => loans.filter((l) => l.isActive), [loans]);

  const cardBalances = useMemo(
    () =>
      activeCards.map((card) => {
        // Compute once per card instead of three times.
        const currentBalance = projectCardBalance(card, plans);
        return {
          card,
          currentBalance,
          available: card.creditLimit - currentBalance,
          utilization: card.creditLimit > 0 ? (currentBalance / card.creditLimit) * 100 : 0,
          minPayment: minimumPaymentForCard(card, currentBalance),
          plan: activeCoveringPlan(card.id, plans),
        };
      }),
    [activeCards, plans]
  );

  const cardTotals = useMemo(() => {
    const totalBalance = cardBalances.reduce((sum, c) => sum + c.currentBalance, 0);
    const totalLimit = activeCards.reduce((sum, c) => sum + c.creditLimit, 0);
    // Was summing the raw `minimumPayment` floor, which disagreed with the
    // computed minimum shown in the Credit Cards view.
    const totalMinPayment = cardBalances.reduce((sum, c) => sum + c.minPayment, 0);
    const totalAvailable = totalLimit - totalBalance;
    const avgRate =
      activeCards.length > 0
        ? activeCards.reduce((sum, c) => sum + c.interestRate, 0) / activeCards.length
        : 0;
    const utilization = totalLimit > 0 ? (totalBalance / totalLimit) * 100 : 0;
    return { totalBalance, totalLimit, totalMinPayment, totalAvailable, avgRate, utilization };
  }, [activeCards, cardBalances]);

  const loanTotals = useMemo(() => {
    const totalBalance = activeLoans.reduce((sum, l) => sum + l.balance, 0);
    const totalOriginal = activeLoans.reduce((sum, l) => sum + l.originalAmount, 0);
    const totalMonthly = activeLoans.reduce((sum, l) => sum + l.monthlyPayment, 0);
    const totalPaid = totalOriginal - totalBalance;
    const avgRate =
      activeLoans.length > 0
        ? activeLoans.reduce((sum, l) => sum + l.interestRate, 0) / activeLoans.length
        : 0;
    const progress = totalOriginal > 0 ? (totalPaid / totalOriginal) * 100 : 0;
    return { totalBalance, totalOriginal, totalMonthly, totalPaid, avgRate, progress };
  }, [activeLoans]);

  const grandTotal = {
    totalDebt: cardTotals.totalBalance + loanTotals.totalBalance,
    totalMonthly: cardTotals.totalMinPayment + loanTotals.totalMonthly,
  };

  const handleExportPDF = () => {
    const el = printRef.current;
    if (!el) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Debt Sheet — ${new Date().toLocaleDateString()}</title>
        <link rel="preconnect" href="https://fonts.googleapis.com">
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
        <link href="https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400..700&family=Instrument+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap" rel="stylesheet">
        <style>
          /* The print window gets none of the app's Tailwind, so Plaster &
             Indigo is restated here in its ink-frugal form: a white sheet,
             darker ink, lighter rules, and no fills at all. Structure comes
             from rule weight — hairline between rows, 1px under a column
             head, 2px ink under a total. */
          *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
          body {
            font-family: 'Instrument Sans', system-ui, sans-serif;
            font-variant-numeric: tabular-nums lining-nums;
            color: #1A1512; background: #FFFFFF; padding: 56px 64px;
          }
          .sheet-header {
            margin-bottom: 26px; padding-bottom: 12px; border-bottom: 2px solid #1A1512;
          }
          .sheet-header h1 {
            font-family: 'Bricolage Grotesque', system-ui, sans-serif;
            font-size: 24px; font-weight: 600; letter-spacing: -0.02em; line-height: 28px;
          }
          .sheet-header p {
            font-family: 'IBM Plex Mono', ui-monospace, monospace;
            font-size: 11px; line-height: 15px; color: #6B5D57; margin-top: 6px;
          }
          .section { margin-bottom: 26px; }
          .section-title {
            font-family: 'IBM Plex Mono', ui-monospace, monospace;
            font-size: 9px; font-weight: 500; letter-spacing: 0.09em;
            text-transform: uppercase; color: #6B5D57; margin-bottom: 8px;
          }
          /* Stats read as a rule-separated band, not as boxes — a printed
             card costs toner and says nothing a rule does not. */
          .stat-row { display: flex; flex-wrap: wrap; gap: 18px; margin-bottom: 26px; }
          .stat { border-left: 1px solid #D6CCC6; padding-left: 12px; min-width: 128px; }
          .stat .value { font-size: 16px; font-weight: 600; line-height: 20px; }
          .stat .label {
            font-family: 'IBM Plex Mono', ui-monospace, monospace;
            font-size: 9px; font-weight: 500; letter-spacing: 0.09em;
            text-transform: uppercase; color: #6B5D57; margin-top: 3px;
          }
          .acct-list { border-top: 1px solid #1A1512; }
          .acct {
            border-bottom: 1px solid #EDE7E3; padding: 7px 0;
            display: flex; flex-wrap: wrap; gap: 12px; align-items: baseline;
          }
          .acct .name { flex: 2 1 200px; font-size: 12px; font-weight: 500; line-height: 16px; }
          .acct .meta {
            font-family: 'IBM Plex Mono', ui-monospace, monospace;
            font-size: 10px; line-height: 14px; color: #6B5D57; margin-top: 3px;
          }
          .acct .fig { width: 116px; text-align: right; font-size: 12px; font-weight: 500; line-height: 16px; }
          .acct .fig .sub {
            font-family: 'IBM Plex Mono', ui-monospace, monospace;
            display: block; font-size: 9px; font-weight: 500; letter-spacing: 0.09em;
            text-transform: uppercase; color: #6B5D57; margin-top: 3px;
          }
          .totals {
            display: flex; flex-wrap: wrap; gap: 18px;
            padding-top: 9px; border-top: 2px solid #1A1512;
          }
          /* Utilisation prints as an outlined meter: filled cells carry ink,
             empty ones carry only their border. */
          .strip { display: flex; gap: 2px; height: 9px; width: 132px; margin-top: 6px; }
          .strip i { flex: 1; display: block; }
          .strip i.on { background: #1A1512; }
          .strip i.on.hot { background: #1A1512; }
          .strip i.off { background: transparent; border: 1px solid #D6CCC6; }
          .foot {
            margin-top: 26px; padding-top: 10px; border-top: 1px solid #D6CCC6;
            font-family: 'IBM Plex Mono', ui-monospace, monospace;
            font-size: 10px; line-height: 14px; color: #6B5D57;
          }
          @media print {
            @page { margin: 1.4cm; }
            body { padding: 0; }
            .no-print { display: none !important; }
            .acct, .stat { break-inside: avoid; }
          }
        </style>
      </head>
      <body>${el.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 300);
  };

  const loanTypeLabel: Record<string, string> = {
    mortgage: 'Mortgage',
    auto: 'Auto',
    student: 'Student',
    personal: 'Personal',
    other: 'Other',
  };

  /** Utilisation / progress as N of 20 cells. Both screen and print read the
   *  same markup, so the strip is plain elements with print-side classes. */
  const strip = (pct: number, hot: boolean) => {
    const on = Math.max(0, Math.min(20, Math.round(Math.min(pct, 100) / 5)));
    return (
      <span className="strip flex gap-[3px] h-[14px] w-[150px] mt-2">
        {Array.from({ length: 20 }).map((_, i) => (
          <i
            key={i}
            className={cn(
              'flex-1 block rounded-sm',
              i < on ? (hot ? 'on hot bg-bad' : 'on bg-accent') : 'off bg-ground'
            )}
          />
        ))}
      </span>
    );
  };

  return (
    <>
      <PageHeader
        heading="Debt Sheet"
        subhead="Every active card and loan on one page, ready to print."
        actions={
          <Button variant="primary" onClick={handleExportPDF} className="no-print">
            Export PDF
          </Button>
        }
      />

      <div ref={printRef} className="mt-11">
        <div className="sheet-header border-b border-rule-base pb-4 mb-9">
          <h1 className={cn(TYPE.hero, 'text-ink')}>Debt Summary Sheet</h1>
          <p className={cn(TYPE.metaTight, 'text-ink-muted mt-2')}>
            Generated {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        <div className="stat-row border-t border-rule-base pt-[18px] mb-9 flex flex-wrap gap-10">
          <div className="stat">
            <div className={cn(TYPE.bigNumber, 'value text-ink')}>{formatCurrency(grandTotal.totalDebt)}</div>
            <div className={cn(TYPE.metaTight, 'label text-ink-muted mt-1.5')}>Total debt</div>
          </div>
          <div className="stat">
            <div className={cn(TYPE.bigNumber, 'value text-ink')}>{formatCurrency(grandTotal.totalMonthly)}</div>
            <div className={cn(TYPE.metaTight, 'label text-ink-muted mt-1.5')}>Monthly payments</div>
          </div>
          <div className="stat">
            <div className={cn(TYPE.bigNumber, 'value text-ink')}>{activeCards.length + activeLoans.length}</div>
            <div className={cn(TYPE.metaTight, 'label text-ink-muted mt-1.5')}>Active accounts</div>
          </div>
        </div>

        {activeCards.length > 0 && (
          <div className="section mb-10">
            <div className={cn(TYPE.sectionLabel, 'section-title text-ink-muted pb-2 mb-3.5 border-b border-rule-base')}>
              Credit Cards
            </div>
            {/* One wrap-safe representation for screen and print — never a
                horizontally scrolling table. */}
            <div className="acct-list bg-surface border border-rule-base rounded-xl overflow-hidden">
              {cardBalances.map(({ card, currentBalance, available, utilization, minPayment }) => (
                <div key={card.id} className="acct flex flex-wrap gap-4 items-baseline px-3.5 py-3.5 border-b border-rule last:border-b-0">
                  <div className="name flex-[2_1_200px] min-w-0">
                    <div className={cn(TYPE.rowTitle, 'text-ink')}>{card.name}</div>
                    <div className={cn(TYPE.metaTight, 'meta text-ink-muted mt-1.5')}>
                      {formatPercent(card.interestRate)} APR
                      <span className="text-ink-muted"> · </span>Limit {formatCurrency(card.creditLimit)}
                      <span className="text-ink-muted"> · </span>{formatCurrency(available)} available
                      {card.dueDay && <><span className="text-ink-muted"> · </span>Due {ordinalDay(card.dueDay)}</>}
                      <span className="text-ink-muted"> · </span>{utilization.toFixed(0)}% used
                    </div>
                    {strip(utilization, utilization >= 75)}
                  </div>
                  <div className="fig w-[120px] text-right shrink-0">
                    <span className={cn(TYPE.rowFigure, "text-ink")}>
                      {formatCurrency(currentBalance)}
                    </span>
                    <span className={cn(TYPE.metaTight, 'sub text-ink-muted block mt-1.5')}>Balance</span>
                  </div>
                  <div className="fig w-[120px] text-right shrink-0">
                    <span className={cn(TYPE.rowFigure, "text-ink")}>
                      {formatCurrency(minPayment)}
                    </span>
                    <span className={cn(TYPE.metaTight, 'sub text-ink-muted block mt-1.5')}>Min payment</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="totals flex flex-wrap gap-10 pt-4">
              <div className="stat">
                <div className={cn(TYPE.bigNumber, 'value text-ink')}>{formatCurrency(cardTotals.totalBalance)}</div>
                <div className={cn(TYPE.metaTight, 'label text-ink-muted mt-1.5')}>Card balance</div>
              </div>
              <div className="stat">
                <div className={cn(TYPE.bigNumber, 'value text-ink')}>{formatCurrency(cardTotals.totalMinPayment)}</div>
                <div className={cn(TYPE.metaTight, 'label text-ink-muted mt-1.5')}>Min payments</div>
              </div>
              <div className="stat">
                <div className={cn(TYPE.bigNumber, 'value text-ink')}>{formatCurrency(cardTotals.totalAvailable)}</div>
                <div className={cn(TYPE.metaTight, 'label text-ink-muted mt-1.5')}>Available</div>
              </div>
              <div className="stat">
                <div className={cn(TYPE.bigNumber, 'value text-ink')}>{formatPercent(cardTotals.avgRate)}</div>
                <div className={cn(TYPE.metaTight, 'label text-ink-muted mt-1.5')}>Average APR</div>
              </div>
            </div>
          </div>
        )}

        {activeLoans.length > 0 && (
          <div className="section mb-10">
            <div className={cn(TYPE.sectionLabel, 'section-title text-ink-muted pb-2 mb-3.5 border-b border-rule-base')}>
              Loans
            </div>
            <div className="acct-list bg-surface border border-rule-base rounded-xl overflow-hidden">
              {activeLoans.map((loan) => {
                const paid = loan.originalAmount - loan.balance;
                const progress = loan.originalAmount > 0 ? (paid / loan.originalAmount) * 100 : 0;
                return (
                  <div key={loan.id} className="acct flex flex-wrap gap-4 items-baseline px-3.5 py-3.5 border-b border-rule last:border-b-0">
                    <div className="name flex-[2_1_200px] min-w-0">
                      <div className={cn(TYPE.rowTitle, 'text-ink')}>{loan.name}</div>
                      <div className={cn(TYPE.metaTight, 'meta text-ink-muted mt-1.5')}>
                        {loanTypeLabel[loan.loanType] || loan.loanType}
                        <span className="text-ink-muted"> · </span>{formatPercent(loan.interestRate)} APR
                        <span className="text-ink-muted"> · </span>Original {formatCurrency(loan.originalAmount)}
                        <span className="text-ink-muted"> · </span>{progress.toFixed(0)}% paid
                        {loan.maturityDate && <><span className="text-ink-muted"> · </span>Matures {formatDate(loan.maturityDate)}</>}
                      </div>
                      {strip(progress, false)}
                    </div>
                    <div className="fig w-[120px] text-right shrink-0">
                      <span className={cn(TYPE.rowFigure, "text-ink")}>
                        {formatCurrency(loan.balance)}
                      </span>
                      <span className={cn(TYPE.metaTight, 'sub text-ink-muted block mt-1.5')}>Balance</span>
                    </div>
                    <div className="fig w-[120px] text-right shrink-0">
                      <span className={cn(TYPE.rowFigure, "text-ink")}>
                        {formatCurrency(loan.monthlyPayment)}
                      </span>
                      <span className={cn(TYPE.metaTight, 'sub text-ink-muted block mt-1.5')}>Monthly</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="totals flex flex-wrap gap-10 pt-4">
              <div className="stat">
                <div className={cn(TYPE.bigNumber, 'value text-ink')}>{formatCurrency(loanTotals.totalBalance)}</div>
                <div className={cn(TYPE.metaTight, 'label text-ink-muted mt-1.5')}>Loan balance</div>
              </div>
              <div className="stat">
                <div className={cn(TYPE.bigNumber, 'value text-ink')}>{formatCurrency(loanTotals.totalOriginal)}</div>
                <div className={cn(TYPE.metaTight, 'label text-ink-muted mt-1.5')}>Original</div>
              </div>
              <div className="stat">
                <div className={cn(TYPE.bigNumber, 'value text-ink')}>{loanTotals.progress.toFixed(0)}%</div>
                <div className={cn(TYPE.metaTight, 'label text-ink-muted mt-1.5')}>Paid down</div>
              </div>
              <div className="stat">
                <div className={cn(TYPE.bigNumber, 'value text-ink')}>{formatCurrency(loanTotals.totalMonthly)}</div>
                <div className={cn(TYPE.metaTight, 'label text-ink-muted mt-1.5')}>Monthly</div>
              </div>
            </div>
          </div>
        )}

        {activeCards.length === 0 && activeLoans.length === 0 && (
          <EmptyState>No debts to display — add credit cards or loans</EmptyState>
        )}

        {(activeCards.length > 0 || activeLoans.length > 0) && (
          <div className={cn(TYPE.metaTight, 'foot text-ink-muted mt-9 pt-4 border-t border-rule-base')}>
            SmartySpend Debt Sheet — {new Date().toLocaleDateString()}
          </div>
        )}
      </div>
    </>
  );
};
